import React, { useMemo } from 'react';
import { createStringFilterFunction } from '../../components/table/functions/createStringFilterFunction';
import { AdminTableInputFilter } from '../../components/table/filters/AdminTableInputFilter';
import { AdminTableColumn } from '../../components/table/interfaces/AdminTableColumn';
import { Transaction } from '../types/Transaction';
import { useTranslation } from 'react-i18next';
import { TransactionsDateColumn } from '../date/column/TransactionsDateColumn';
import { createTransactionsDateFilterFunction } from '../date/filter/createTransactionsDateFilterFunction';
import { TransactionPropertiesGroup } from '../date/filter/TransactionPropertiesGroup';
import { TransactionsDateReturnedFilter } from '../date/filter/TransactionsDateReturnedFilter';
import { TransactionsDateLeftFilter } from '../date/filter/TransactionsDateLeftFilter';
import { TransactionStatusIcon } from '../components/TransactionStatusIcon';

export function useTransactionColumns(): AdminTableColumn<Transaction>[] {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        title: '',
        predicate: (item) => <TransactionStatusIcon status={item.status} />,
        sortingPredicate: (item) => item.status,
        style: {
          width: 40,
          textAlign: 'center',
        },
      },
      {
        title: t('Type').toString(),
        predicate: (item) => (item.type === 'transfer' ? t('Transfer') : t('Checkout')),
        sortingPredicate: (item) => item.type,
        filterConfig: {
          filterFn: createStringFilterFunction((item) => item.type),
          component: AdminTableInputFilter,
        },
        style: {
          width: 100,
        },
      },
      {
        title: t('Asset').toString(),
        predicate: (item) => item.assetGuid,
        sortingPredicate: (item) => item.assetGuid,
        filterConfig: {
          filterFn: createStringFilterFunction((item) => item.assetGuid),
          component: AdminTableInputFilter,
        },
      },
      {
        title: t('Customer').toString(),
        predicate: (item) => item.customerName ?? '-',
        sortingPredicate: (item) => item.customerName ?? '',
        filterConfig: {
          filterFn: createStringFilterFunction((item) => item.customerName ?? ''),
          component: AdminTableInputFilter,
        },
      },
      {
        title: t('From site').toString(),
        predicate: (item) => item.sourceSiteName ?? '-',
        sortingPredicate: (item) => item.sourceSiteName ?? '',
        filterConfig: {
          filterFn: createStringFilterFunction((item) => item.sourceSiteName ?? ''),
          component: AdminTableInputFilter,
        },
      },
      {
        title: t('To site').toString(),
        predicate: (item) => item.destinationSiteName ?? '-',
        sortingPredicate: (item) => item.destinationSiteName ?? '',
        filterConfig: {
          filterFn: createStringFilterFunction((item) => item.destinationSiteName ?? ''),
          component: AdminTableInputFilter,
        },
      },
      {
        title: t('Left').toString(),
        predicate: (item) => (
          <TransactionsDateColumn
            startedAt={item.leftAt}
            approvedBy={item.leftApprovedBy}
            approvedAt={item.leftApprovedAt}
            isApproved={item.status !== 'pending_checkout'}
          />
        ),
        sortingPredicate: (item) => item.leftAt,
        filterConfig: {
          filterFn: createTransactionsDateFilterFunction(TransactionPropertiesGroup.Left),
          component: TransactionsDateLeftFilter,
        },
        style: {
          width: 200,
        },
      },
      {
        title: t('Returned').toString(),
        predicate: (item) => (
          <TransactionsDateColumn
            startedAt={item.returnedAt}
            approvedBy={item.returnApprovedBy}
            approvedAt={item.returnApprovedAt}
            isApproved={item.status === 'completed'}
          />
        ),
        sortingPredicate: (item) => item.returnedAt,
        filterConfig: {
          filterFn: createTransactionsDateFilterFunction(TransactionPropertiesGroup.Returned),
          component: TransactionsDateReturnedFilter,
        },
        style: {
          width: 200,
        },
      },
    ],
    [t],
  );
}
